import React, { useRef, SyntheticEvent, InputHTMLAttributes } from 'react';
import { ReactCrop, ReactCropProps, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { ImageCropDialog } from './ImageCropDialog';
import { useImageCropContext } from './ImageCropContext';
import { ImageCropDialogHeader } from './ImageCropDialogHeader';
import { ImageCropRemoveButton } from './ImageCropRemoveButton';
import { ImageCropDialogActions } from './ImageCropDialogActions';
import { ImageCropSelectButton, ImageCropSelectButtonProps } from './ImageCropSelectButton';
import {
    ImageCropStrings,
    centerCropByAspectRatio,
    defaultAspectRatio,
    defaultMinimumCropWidth,
    defaultAllowedImageTypes,
} from './ImageCropUtils';

export interface ImageCropInnerProps
    extends
        Pick<ImageCropSelectButtonProps, 'className'>,
        Pick<ReactCropProps, 'aspect' | 'circularCrop'>,
        Pick<HTMLImageElement, 'src'> {
    strings: ImageCropStrings;
    inputProps?: InputHTMLAttributes<HTMLInputElement>;
    validateCallback: (value: Blob) => boolean;
}

export const ImageCropInner = ({
    className,
    src,
    aspect,
    circularCrop,
    strings,
    inputProps,
    validateCallback,
}: ImageCropInnerProps): React.JSX.Element => {
    const inputRef = useRef<HTMLInputElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imagePreviewRef = useRef<HTMLImageElement>(null);
    const {
        openDialog,
        validationError,
        processing,
        selectedImage,
        crop,
        setOpenDialog,
        setSelectedImage,
        setCrop,
        setPixelCrop,
        uploadImageAsync,
        resetImage,
    } = useImageCropContext();

    const { selectButtonTooltipText, removeButtonTooltipText, uploadButtonText, dialogHeaderText, dialogContentText } =
        strings;

    const isImageSet = src !== '';
    const aspectRatio = aspect || defaultAspectRatio;

    const focusFileInput = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.click();
        }
    };

    const handleFileRead = (reader: FileReader, type: string) => {
        const result = reader.result?.toString() ?? '';

        if (result) {
            setSelectedImage(result, type);
        }
    };

    const handleFileChange = () => {
        if (inputRef.current) {
            const { files } = inputRef.current;

            if (files && files.length > 0) {
                const reader = new FileReader();
                const file = files[0];

                if (validateCallback(file)) {
                    reader.addEventListener('load', () => handleFileRead(reader, file.type));
                    reader.readAsDataURL(file);

                    reader.removeEventListener('load', () => handleFileRead(reader, file.type));
                }
            }
        }
    };

    const handleFileUpload = async () => {
        if (imagePreviewRef.current && canvasRef.current) {
            await uploadImageAsync(imagePreviewRef.current, canvasRef.current);
        }
    };

    const handleInitialCrop = (event: SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = event.currentTarget;
        const percentCrop = centerCropByAspectRatio(width, height, aspectRatio);
        setCrop(percentCrop);
        setPixelCrop(convertToPixelCrop(percentCrop, width, height));
    };

    return (
        <>
            <ImageCropSelectButton
                selectButtonTooltipText={selectButtonTooltipText}
                removeButton={
                    <ImageCropRemoveButton
                        processing={processing}
                        removeButtonTooltipText={removeButtonTooltipText}
                        onClick={resetImage}
                    />
                }
                onClick={focusFileInput}
                disabled={isImageSet}
                className={className}
            >
                <img
                    data-cy="ImageCropCurrentImage"
                    src={src}
                    alt=""
                    className="max-h-max"
                    style={{
                        height: 'inherit',
                        width: 'inherit',
                    }}
                />
            </ImageCropSelectButton>
            {!isImageSet && (
                <>
                    <canvas ref={canvasRef} hidden />
                    <input
                        ref={inputRef}
                        type="file"
                        accept={defaultAllowedImageTypes.join(', ')}
                        {...inputProps}
                        onChange={handleFileChange}
                        hidden
                    />
                </>
            )}
            <ImageCropDialog
                dialogContentText={dialogContentText}
                open={openDialog}
                validationError={validationError}
                header={
                    <ImageCropDialogHeader
                        processing={processing}
                        onClick={() => setOpenDialog(false)}
                        dialogHeaderText={dialogHeaderText}
                    />
                }
                actions={
                    <ImageCropDialogActions
                        processing={processing}
                        onUpload={handleFileUpload}
                        uploadButtonText={uploadButtonText}
                    />
                }
            >
                {selectedImage && (
                    <ReactCrop
                        crop={crop}
                        keepSelection
                        circularCrop={circularCrop}
                        disabled={processing}
                        aspect={aspectRatio}
                        onChange={(pixelCrop, percentCrop) => {
                            setCrop(percentCrop);
                            setPixelCrop(pixelCrop);
                        }}
                        minWidth={defaultMinimumCropWidth}
                    >
                        <img
                            ref={imagePreviewRef}
                            src={selectedImage}
                            onLoad={handleInitialCrop}
                            className="h-60"
                            alt=""
                        />
                    </ReactCrop>
                )}
            </ImageCropDialog>
        </>
    );
};
